import * as React from 'react';
import { OutsideClick } from '@cian/outside-click-component';
import { EMaxAuctionService } from '@cian/api-models/search-offers/v2/search-offers-desktop.data';
import * as styles from './AuctionPopup.css';
import { IAuction } from '../../../types/offer';
import { AuctionBetManager } from '../AuctionBetManager';
import * as PropTypes from 'prop-types';
import { trackAuctionHint, THintType } from '../AuctionBetManager/tracking';

export enum AuctionPopupKind {
  /** Объявление принадлежит пользователю и ставку можно изменить  */
  MANAGE,
  /** Объявление не принадлежит пользователю, но есть на других страницах выдачи */
  AUCTION,
  /** Объявление не принадлежит пользователю, но его объявление есть на этой странице выдачи  */
  AUCTION_ON_THIS_PAGE,
  /** Объявление принадлежит пользователю и ставку нельзя изменить */
  NOT_PREMIUM,
  /**
   * Объявление не принадлежит пользователю, но его объявление есть на этой странице выдачи
   * и имеет отличный от текущего отбъявления пакет услуг
   */
  AUCTION_DIFFERENT_SERVICE,
}

export type TAuctionPopupKind = AuctionPopupKind | null;

export interface IAuctionPopupProps {
  kind: TAuctionPopupKind;
  initialBet: number;
  isFromOwn?: boolean;
  onClose(): void;
}

const POPUP_ANALYTICS_MAP: { [index: number]: THintType } = {
  [AuctionPopupKind.MANAGE]: 'up_search',
  [AuctionPopupKind.AUCTION]: 'no_offer',
  [AuctionPopupKind.AUCTION_ON_THIS_PAGE]: 'with_offer',
  [AuctionPopupKind.NOT_PREMIUM]: 'not_premium',
};

export class AuctionPopup extends React.Component<IAuctionPopupProps, Object> {
  public static contextTypes = {
    isMlSearchForAll: PropTypes.bool,
    offer: PropTypes.object,
    maxAuctionBet: PropTypes.number,
    maxAuctionService: PropTypes.string,
  };

  public componentWillMount() {
    const { offer } = this.context;
    const { kind } = this.props;
    const hintType = POPUP_ANALYTICS_MAP[kind as AuctionPopupKind];

    trackAuctionHint(hintType, 'show', offer);
  }

  public render() {
    const { offer, maxAuctionService, maxAuctionBet } = this.context;
    const { kind, initialBet, isFromOwn = false, onClose } = this.props;

    const { auction } = offer;

    const currentServiceType = this.getCurrentOfferServiceType();

    return (
      <OutsideClick onOutsideClick={onClose}>
        <div className={styles['popup']}>
          <div className={styles['close']} onClick={onClose} />

          {kind === AuctionPopupKind.MANAGE &&
            <AuctionBetManager
              initialBet={this.getAuctionBet(initialBet)}
              isFromOwn={isFromOwn}
              onClose={onClose}
              auctionBetCalculator={this.getAuctionBet}
            />
          }

          {kind === AuctionPopupKind.AUCTION_ON_THIS_PAGE &&
            <>
              Чтобы опередить это<br />
              объявление,&nbsp;
              <a
                className={styles['raise_bet_link']}
                onClick={this.handleRaiseBetLinkClick(auction)}
              >
                повысьте ставку
              </a>
            </>
          }

          {auction && kind === AuctionPopupKind.AUCTION &&
            <>
              Чтобы опередить это объявление,<br />
              опубликуйте {this.getAdTypeName()}объявление<br />
              со ставкой больше {auction.currentBet} ₽
            </>
          }

          {kind === AuctionPopupKind.AUCTION_DIFFERENT_SERVICE &&
            <>
              Чтобы опередить это объявление,<br />
              <a
                href={this.getAdLink(auction.userOfferId, currentServiceType, auction.currentBet + auction.stepBet)}
                className={styles['raise_bet_link']}
                target="_blank"
              >
                опубликуйте
              </a>
              {' '}
              ваше объявление<br />
              со ставкой {auction.currentBet + auction.stepBet} ₽.
            </>
          }

          {kind === AuctionPopupKind.NOT_PREMIUM &&
            <>
              Хотите поднять объявление на первую<br />
              страницу поиска?<br />
              <a
                href={this.getAdLink(offer.id, maxAuctionService, maxAuctionBet)}
                className={styles['raise_bet_link']}
                target="_blank"
                onClick={this.handlePublishOfferClick}
              >
                Опубликуйте его
              </a>&nbsp;
              как {this.getAuctionServiceName(maxAuctionService)}<br />
              со ставкой больше {this.getAuctionBet(maxAuctionBet)} ₽.
            </>
          }
        </div>
      </OutsideClick>
    );
  }

  private getAdLink = (id: string, service: string, bet: number) => {
    const { isMlSearchForAll } = this.context;

    return `/razmestit-obyavlenie/${id}/?auctionBet=${bet}`
      + (isMlSearchForAll ? `&auctionService=${service}` : '');
  }

  private getAuctionServiceName = (maxAuctionService: EMaxAuctionService | undefined): string => {
    switch (maxAuctionService) {
      case EMaxAuctionService.top:
        return 'Топ-объявление' ;
      case EMaxAuctionService.premium:
        return 'Премиум-объявление';
      case EMaxAuctionService.paid:
      default:
        return 'Платное объявление';
    }
  }

  private getAuctionBet = (value?: number | null) => {
    const { offer: {auction} } = this.context;
    const { stepBet } = auction;

    if (value) {
      return value;
    }

    return stepBet;
  }

  private getAdTypeName = () => {
    const { offer } = this.context;

    switch (true) {
      case offer.isPremium:
        return 'Премиум-';

      case offer.isTop3:
        return 'Топ-';

      case offer.isPaid:
        return 'Платное ';

      default:
        return '';
    }
  }

  public componentWillUnmount() {
    const { offer } = this.context;
    const { kind } = this.props;
    const hintType = POPUP_ANALYTICS_MAP[kind as AuctionPopupKind];

    trackAuctionHint(hintType, 'close', offer);
  }

  private handleRaiseBetLinkClick = (auction: IAuction) => () => {
    const { userOfferId, currentBet } = auction;
    const { offer } = this.context;

    if (!userOfferId) {
      return;
    }

    const event = new CustomEvent('auctionBetManagerShow', { detail: { currentBet, userOfferId } });

    document.dispatchEvent(event);

    this.props.onClose();

    trackAuctionHint('with_offer', 'click', offer);
  }

  private handlePublishOfferClick = () => {
    const { offer } = this.context;

    trackAuctionHint('not_premium', 'click', offer);
  }

  private getCurrentOfferServiceType = () => {
    const { offer } = this.context;

    switch (true) {
      case offer.isTop3:
        return 'top3';

      case offer.isPremium:
        return 'premium';

      case offer.isPaid:
      default:
        return 'paid';
    }
  }
}
